import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TopTabbar from "../components/partials/TopTabbar";
import Header from "../components/partials/Header";
import Footer from "../components/partials/Footer";
import UserService from '../services/UserService';
import AuthService from '../services/AuthService';
import LoadingSpinner from '../utils/LoadingSpinner';

// Données pour les provinces canadiennes
const provincesCanada = [
    'Alberta',
    'Colombie-Britannique',
    'Manitoba',
    'Nouveau-Brunswick',
    'Terre-Neuve-et-Labrador',
    'Territoires du Nord-Ouest',
    'Nouvelle-Écosse',
    'Nunavut',
    'Ontario',
    'Île-du-Prince-Édouard',
    'Québec',
    'Saskatchewan',
    'Yukon'
];

// Données pour les villes par province (exemple simplifié)
const citiesCanada = {
    'Québec': ['Montréal', 'Québec', 'Laval', 'Gatineau', 'Longueuil', 'Sherbrooke', 'Saguenay', 'Lévis', 'Trois-Rivières', 'Terrebonne'],
    'Ontario': ['Toronto', 'Ottawa', 'Mississauga', 'Brampton', 'Hamilton', 'London', 'Markham', 'Vaughan', 'Kitchener', 'Windsor'],
    'Alberta': ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge', 'St. Albert', 'Medicine Hat', 'Grande Prairie', 'Airdrie'],
    'Colombie-Britannique': ['Vancouver', 'Surrey', 'Burnaby', 'Richmond', 'Abbotsford', 'Coquitlam', 'Kelowna', 'Saanich'],
};

const ProfilePage = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirthString: '',
        profession: '',
        streetAddress: '',
        neighborhood: '',
        city: '',
        province: '',
        country: 'Canada',
        additionalInfos: ''
    });
    const [originalFormData, setOriginalFormData] = useState(null);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [profileImage, setProfileImage] = useState(null);
    const [base64Image, setBase64Image] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [originalRoleNames, setOriginalRoleNames] = useState([]);
    const [isUserVerified, setIsUserVerified] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [citySuggestions, setCitySuggestions] = useState([]);
    const [errors, setErrors] = useState({});

    // Validation en temps réel
    const validateField = (name, value, currentFormData = formData) => {
        let error = '';
        // Champs obligatoires (sauf profession et additionalInfos)
        const requiredFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'dateOfBirthString', 'city', 'province'];
        if (requiredFields.includes(name) && (!value || !value.toString().trim())) {
            error = 'Ce champ est obligatoire';
        }
        // Validation spécifique par champ
        switch (name) {
            case 'email':
                if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    error = 'Format d\'email invalide';
                }
                break;
            case 'phoneNumber':
                if (value && !/^\d{10}$/.test(value.toString())) {
                    error = 'Le numéro doit contenir exactement 10 chiffres';
                }
                break;
            case 'dateOfBirthString':
                if (value) {
                    const birthDate = new Date(value);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                        age--;
                    }
                    if (age < 18) {
                        error = 'Vous devez avoir au moins 18 ans';
                    }
                }
                break;
            case 'province':
                if (value && !provincesCanada.includes(value)) {
                    error = 'Veuillez choisir une province dans la liste';
                }
                break;
            // Suppression de la validation pour la ville - l'utilisateur peut saisir n'importe quelle ville
        }
        return error;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Gestion spéciale pour le numéro de téléphone
        if (name === 'phoneNumber') {
            // Ne garder que les chiffres
            processedValue = value.replace(/\D/g, '');
            // Limiter à 10 chiffres
            if (processedValue.length > 10) {
                processedValue = processedValue.slice(0, 10);
            }
        }

        // Mise à jour du formData
        const newFormData = { ...formData, [name]: processedValue };
        setFormData(newFormData);

        // Validation en temps réel avec les nouvelles données
        const error = validateField(name, processedValue, newFormData);
        setErrors((prevErrors) => ({ ...prevErrors, [name]: error }));

        // Réinitialiser les suggestions de ville si la province change
        if (name === 'province') {
            setFormData((prevData) => ({ ...prevData, province: processedValue, city: '' }));
            setErrors((prevErrors) => ({ ...prevErrors, city: '' }));
            setCitySuggestions([]);
        }
    };

    const handleProvinceChange = (e) => {
        handleInputChange(e);
        const text = e.target.value;
        if (text.trim()) {
            const filteredSuggestions = provincesCanada
                .filter((province) => province.toLowerCase().includes(text.toLowerCase()))
                .slice(0, 6);
            setSuggestions(filteredSuggestions);
        } else {
            setSuggestions([]);
        }
    };

    const handleProvinceSuggestionClick = (province) => {
        const newFormData = { ...formData, province, city: '' };
        setFormData(newFormData);
        setSuggestions([]);
        setCitySuggestions([]);
        setErrors((prevErrors) => ({ ...prevErrors, province: '', city: '' }));
    };

    const handleCityChange = (e) => {
        const { name, value } = e.target;

        // Mise à jour du formData sans validation restrictive
        const newFormData = { ...formData, [name]: value };
        setFormData(newFormData);

        // Effacer toute erreur précédente pour la ville
        setErrors((prevErrors) => ({ ...prevErrors, [name]: '' }));

        // Afficher les suggestions si la province est sélectionnée
        const text = e.target.value;
        if (formData.province && citiesCanada[formData.province] && text.trim()) {
            const filteredCities = citiesCanada[formData.province]
                .filter((city) => city.toLowerCase().includes(text.toLowerCase()))
                .slice(0, 6);
            setCitySuggestions(filteredCities);
        } else {
            setCitySuggestions([]);
        }
    };

    const handleCitySuggestionClick = (city) => {
        const newFormData = { ...formData, city };
        setFormData(newFormData);
        setCitySuggestions([]);
        setErrors((prevErrors) => ({ ...prevErrors, city: '' }));
    };

    // Validation du formulaire
    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        // Valider tous les champs obligatoires
        const requiredFields = ['firstName', 'lastName', 'email', 'phoneNumber', 'dateOfBirthString', 'city', 'province'];
        requiredFields.forEach(key => {
            const error = validateField(key, formData[key], formData);
            if (error) {
                newErrors[key] = error;
                isValid = false;
            }
        });

        // Valider aussi les champs optionnels s'ils sont remplis
        ['profession', 'additionalInfos'].forEach(key => {
            if (formData[key]) {
                const error = validateField(key, formData[key], formData);
                if (error) {
                    newErrors[key] = error;
                    isValid = false;
                }
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = AuthService.getCurrentUser();
            if (currentUser && currentUser.username) {
                const username = currentUser.username;
                try {
                    const isVerified = await UserService.checkIdentification(username, 'fr');
                    setIsUserVerified(isVerified);
                    const userId = currentUser.id;
                    const data = await UserService.findById(userId);
                    const filteredData = {
                        id: data.id,
                        firstName: data.firstName || '',
                        lastName: data.lastName || '',
                        email: data.email || '',
                        phoneNumber: data.phoneNumber || '',
                        dateOfBirthString: data.dateOfBirthString || '',
                        profession: data.profession || '',
                        streetAddress: data.streetAddress || '',
                        neighborhood: data.neighborhood || '',
                        city: data.city || '',
                        province: data.province || '',
                        country: data.country || 'Canada',
                        username: data.username || '',
                        password: data.password || '',
                        additionalInfos: data.additionalInfos || '',
                        roleNames: data.roleNames || [],
                        profileImageUrl: data.profileImageUrl || '',
                        idDocsVerified: isVerified
                    };
                    setFormData(filteredData);
                    setOriginalFormData(filteredData);
                    setOriginalRoleNames(data.roleNames || []);
                    if (filteredData.profileImageUrl && filteredData.profileImageUrl.startsWith('data:image')) {
                        setProfileImage(filteredData.profileImageUrl);
                    } else {
                        setProfileImage(filteredData.profileImageUrl || "assets/images/invite-friend/avatar.jpg");
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement des données utilisateur:', error);
                    toast.error("Impossible de charger les informations de l'utilisateur.");
                } finally {
                    setIsPageLoading(false);
                }
            } else {
                toast.error("Utilisateur non connecté !");
                setIsPageLoading(false);
            }
        };
        fetchUserData();
    }, []);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancel = () => {
        setFormData(originalFormData);
        setIsEditing(false);
        setProfileImage(originalFormData?.profileImageUrl || "assets/images/invite-friend/avatar.jpg");
        setBase64Image(null);
        setErrors({});
        setSuggestions([]);
        setCitySuggestions([]);
    };

    const handleSave = async () => {
        if (isSaving) {
            return;
        }
        if (!validateForm()) {
            toast.error('Veuillez corriger les erreurs dans le formulaire');
            return;
        }

        const result = await Swal.fire({
            title: "Confirmer les modifications",
            text: "Êtes-vous sûr de vouloir appliquer ces modifications ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Oui, modifier",
            cancelButtonText: "Annuler"
        });

        if (result.isConfirmed) {
            await performSave();
        }
    };

    // Fonction séparée pour effectuer la sauvegarde
    const performSave = async () => {
        setIsSaving(true);
        try {
            const dataToUpdate = {
                ...formData,
                roleNames: originalRoleNames,
                profileImageUrl: base64Image
                    ? `data:image/jpeg;base64,${base64Image}`
                    : formData.profileImageUrl
            };

            const response = await UserService.updateUser(dataToUpdate);

            if (response && (response.success === true || !response.hasOwnProperty('success'))) {
                toast.success("Profil mis à jour avec succès!");
                setOriginalFormData(dataToUpdate);
                setIsEditing(false);
                setBase64Image(null);
                setErrors({});
            } else {
                throw new Error(response?.message || "La mise à jour a échoué");
            }
        } catch (error) {
            console.error("Erreur lors de la mise à jour:", error);
            let errorMessage = 'Échec de la mise à jour du profil';
            if (error.response) {
                errorMessage = error.response.data?.message || `Erreur serveur: ${error.response.status}`;
            } else if (error.request) {
                errorMessage = 'Erreur de connexion. Vérifiez votre connexion internet.';
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileImage(reader.result);
                // Extraire le base64 sans le préfixe
                const base64String = reader.result.split(',')[1];
                setBase64Image(base64String);
            };
            reader.readAsDataURL(file);
        }
    };

    const fieldLabels = {
        firstName: "Prénom",
        lastName: "Nom",
        email: "Email",
        dateOfBirthString: "Date de naissance",
        phoneNumber: "Numéro de téléphone",
        country: "Pays",
        province: "Province",
        city: "Ville",
        profession: "Profession",
        additionalInfos: "Informations supplémentaires"
    };

    // Style pour les champs avec erreur
    const getInputStyle = (fieldName) => {
        let className = '';
        const isDisabled = ['country', ...(isUserVerified ? ['firstName', 'lastName', 'dateOfBirthString'] : [])].includes(fieldName);
        if (isDisabled) className += ' disabled-input';
        if (errors[fieldName]) className += ' error-input';
        return className;
    };

    if (isPageLoading) {
        return <LoadingSpinner isVisible={true} loadingText="Chargement des données..." />;
    }

    return (
        <>
            <div className="d-flex flex-column vh-100">
                <div className="flex-grow-1 overflow-y-scroll">
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '800px',
                        margin: '0 auto'
                    }}>
                        <div className="verify-number-main">
                            <TopTabbar userName={formData?.firstName} />
                            <Header />
                            <div className="verify-number-top">
                                <div className="container">
                                    <div className="verify-number-top-content">
                                        <h3 className="name" style={{
                                            fontWeight: 'bold',
                                            fontSize: '24px',
                                            color: 'white',
                                        }}>Informations personnelles</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="verify-number-bottom" id="personal-info">
                                <div className="verify-number-bottom-wrap">
                                    <div className="verify-number-content">
                                        <h1 className="d-none">Informations personnelles</h1>
                                        <div className="personal-info-main">
                                            <div className="profile-edit-first">
                                                <div className="profile-edit-img">
                                                    <img style={{
                                                        width: '100px',
                                                        height: '100px',
                                                        borderRadius: '50%',
                                                    }} src={profileImage} alt="profile-img" className="profile-pic" />
                                                    <div className="image-input">
                                                        <input type="file" accept="image/*" id="imageInput" className="file-upload" disabled={!isEditing} onChange={handleImageChange} />

                                                        {/*Bouton d'upload de l'images de profil*/}
                                                        {/*{isEditing && (
                                                            <label htmlFor="imageInput" className="image-button">
                                                                <img src="assets/svg/camera-icon.svg" alt="camera-icon" className="upload-button" />
                                                            </label>
                                                        )}*/}


                                                    </div>
                                                </div>
                                            </div>
                                            {!isEditing && (
                                                <div className="mb-3">
                                                    <button type="button" className="btn btn-primary" onClick={handleEditClick}>
                                                        <i className="fa-duotone fa-solid fa-pen-to-square me-2"></i>
                                                        Modifier mes informations
                                                    </button>
                                                </div>
                                            )}
                                            <form className="personal-info-form mt-24" style={{ marginBottom: '80px' }}>
                                                {/* Champ Numéro de téléphone avec indicatif - CORRIGÉ */}
                                                <div className="personal-name mt-16">
                                                    <label htmlFor="phoneNumber">Numéro de téléphone</label>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <div style={{
                                                            padding: '10px 15px',
                                                            backgroundColor: 'rgba(236,229,229,0.3)',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '4px 0 0 4px',
                                                            marginRight: '0',
                                                            height: '38px',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}>
                                                            🇨🇦 +1
                                                        </div>
                                                        <input
                                                            type="text"
                                                            name="phoneNumber"
                                                            id="phoneNumber"
                                                            value={formData?.phoneNumber || ''}
                                                            disabled={!isEditing}
                                                            onChange={handleInputChange}
                                                            className={getInputStyle('phoneNumber')}
                                                            placeholder="4185551234"
                                                            maxLength="10"
                                                            style={{
                                                                borderRadius: '0 4px 4px 0',
                                                                flex: 1,
                                                                marginLeft: '0',
                                                                height: '38px'
                                                            }}
                                                            onKeyDown={(e) => {
                                                                // Autoriser uniquement les chiffres, backspace, tab, etc.
                                                                if (!/[0-9]/.test(e.key) &&
                                                                    !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {errors.phoneNumber && (
                                                        <small className="error-message">{errors.phoneNumber}</small>
                                                    )}
                                                </div>
                                                {Object.keys(fieldLabels).filter(key => key !== 'phoneNumber').map((key) => {
                                                    if (key === 'id') return null;
                                                    const isDisabled = !isEditing || (isUserVerified && ['firstName', 'lastName', 'dateOfBirthString'].includes(key)) || key === 'country';
                                                    return (
                                                        <div className="personal-name mt-16" key={key} style={{ position: 'relative' }}>
                                                            <label htmlFor={key}>{fieldLabels[key]}</label>
                                                            {key === 'dateOfBirthString' && isEditing ? (
                                                                <input
                                                                    type="date"
                                                                    name={key}
                                                                    id={key}
                                                                    value={formData?.[key] || ''}
                                                                    disabled={isDisabled}
                                                                    onChange={handleInputChange}
                                                                    className={getInputStyle(key)}
                                                                    max={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()).toISOString().split('T')[0]}
                                                                    min="1900-01-01"
                                                                />
                                                            )  : key === 'province' && isEditing ? (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        name={key}
                                                                        id={key}
                                                                        value={formData?.[key] || ''}
                                                                        disabled={isDisabled}
                                                                        onChange={handleProvinceChange}
                                                                        className={getInputStyle(key)}
                                                                        placeholder="Tapez pour chercher une province..."
                                                                        autoComplete="off"
                                                                    />
                                                                    {suggestions.length > 0 && (
                                                                        <div className="suggestions-container">
                                                                            {suggestions.map((province, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="suggestion-item"
                                                                                    onClick={() => handleProvinceSuggestionClick(province)}
                                                                                >
                                                                                    {province}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : key === 'city' && isEditing ? (
                                                                <>
                                                                    <input
                                                                        type="text"
                                                                        name={key}
                                                                        id={key}
                                                                        value={formData?.[key] || ''}
                                                                        disabled={!formData.province || isDisabled}
                                                                        onChange={handleCityChange}
                                                                        className={getInputStyle(key)}
                                                                        placeholder={formData.province ? "Saisissez votre ville" : "Sélectionnez d'abord une province"}
                                                                        autoComplete="off"
                                                                    />
                                                                    {citySuggestions.length > 0 && (
                                                                        <div className="suggestions-container">
                                                                            {citySuggestions.map((city, index) => (
                                                                                <div
                                                                                    key={index}
                                                                                    className="suggestion-item"
                                                                                    onClick={() => handleCitySuggestionClick(city)}
                                                                                >
                                                                                    {city}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : key === 'additionalInfos' ? (
                                                                <textarea
                                                                    name={key}
                                                                    id={key}
                                                                    value={formData?.[key] || ''}
                                                                    disabled={isDisabled}
                                                                    onChange={handleInputChange}
                                                                    className={getInputStyle(key)}
                                                                    rows="3"
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    name={key}
                                                                    id={key}
                                                                    value={formData?.[key] || ''}
                                                                    disabled={isDisabled}
                                                                    onChange={handleInputChange}
                                                                    className={getInputStyle(key)}
                                                                />
                                                            )}
                                                            {errors[key] && (
                                                                <small className="error-message">{errors[key]}</small>
                                                            )}
                                                            {isDisabled && isEditing && (
                                                                <small className="identification-notice">
                                                                    {key === 'country'
                                                                        ? "Ce champ ne peut pas être modifié."
                                                                        : "Ce champ ne peut pas être modifié après vérification de votre identité."}
                                                                </small>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {isEditing && (
                                                    <div className="mt-16 text-center" style={{ borderTop: '2px dashed rgb(133 121 121)' }}>
                                                        <button
                                                            type="button"
                                                            className="btn btn-success me-2 mt-3"
                                                            onClick={handleSave}
                                                            disabled={isSaving}
                                                        >
                                                            {isSaving ? (
                                                                <span>
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    En cours...
                                                                </span>
                                                            ) : (
                                                                "Valider"
                                                            )}
                                                        </button>
                                                        {!isSaving && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger mt-3"
                                                                onClick={handleCancel}
                                                            >
                                                                Annuler
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Footer />
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .disabled-input {
                    background-color: #f0f0f0;
                    cursor: not-allowed;
                }
                .identification-notice {
                    color: #888;
                    font-size: 0.875em;
                    display: block;
                    margin-top: 0.25em;
                }
                .error-input {
                    border-color: #dc3545 !important;
                    border-width: 1px !important;
                }
                .error-message {
                    color: #dc3545;
                    font-size: 0.875em;
                    display: block;
                    margin-top: 0.25em;
                }
                .suggestions-container {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #ddd;
                    border-top: none;
                    z-index: 1000;
                    max-height: 200px;
                    overflow-y: auto;
                }
                .suggestion-item {
                    padding: 10px;
                    cursor: pointer;
                    border-bottom: 1px solid #eee;
                }
                .suggestion-item:hover {
                    background-color: #f8f9fa;
                }
                .profile-edit-img {
                    position: relative;
                    margin-bottom: 20px;
                }
                .image-input {
                    position: absolute;
                    bottom: 0;
                    right: 0;
                }
                .file-upload {
                    display: none;
                }
                .image-button {
                    background-color: #007bff;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }
                .upload-button {
                    width: 20px;
                    height: 20px;
                }
                .personal-name {
                    margin-bottom: 1rem;
                }
                .personal-name label {
                    display: block;
                    margin-bottom: 0.5rem;
                    font-weight: bold;
                }
                .personal-name input,
                .personal-name select,
                .personal-name textarea {
                    width: 100%;
                    padding: 0.5rem;
                    border: 1px solid #ccc;
                    border-radius: 4px;
                    font-size: 1rem;
                }
                .personal-name textarea {
                    resize: vertical;
                }
                .btn {
                    padding: 0.5rem 1rem;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 1rem;
                }
                .btn-primary {
                    background-color: #007bff;
                    color: white;
                }
                .btn-success {
                    background-color: #28a745;
                    color: white;
                }
                .btn-danger {
                    background-color: #dc3545;
                    color: white;
                }
                .btn:disabled {
                    opacity: 0.65;
                    cursor: not-allowed;
                }
                .personal-name input:disabled {
                    background-color: #f8f9fa;
                    cursor: not-allowed;
                    opacity: 0.7;
                }
                .personal-name input:focus {
                    border-color: #80bdff;
                    outline: 0;
                    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
                }
            `}</style>
        </>
    );
};

export default ProfilePage;